import React, { FunctionComponent, MouseEvent, useState, useRef, useEffect } from 'react';
import {
    makeStyles,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    IconButton,
    Box,
    Tooltip,
    Fab,
    Typography,
    Collapse,
} from '@material-ui/core';
import moment from 'moment';
import { Delete, Close, KeyboardArrowDown, KeyboardArrowUp } from '@material-ui/icons';
import EditIcon from '@material-ui/icons/Edit';
import TableSortLabel from '@material-ui/core/TableSortLabel';
import { v1 as uuid } from 'uuid';
import { AutoSizer } from 'react-virtualized';
import ArrowDown from '@material-ui/icons/ArrowDropDown';
import BackToTopArrow from '@material-ui/icons/ArrowUpward';
import { displayCurrencyAmount } from '../../utils/utils';

type DataTableProps = {
    className?: string;
    columns: any;
    data: Array<unknown>;
    count: number;
    handleDeleteCase?: Function;
    handleDownloadCase?: Function;
    onClickRow: Function;
    deleteFlag?: boolean;
    getAppIcon?: boolean;
    editFlag?: boolean;
    classes?: any;
    sortBy?: any;
    sortOrder: Order;
    removeFlag?: boolean;
    removeTooltipLabel?: string;
    showDetailsLabel?: string;
    handleRemove?: Function;
    setSortOrder?: Function;
    renderDetails?: Function;
    setSortColumn?: Function;
};
const useStyles = makeStyles({
    root: {
        padding: '0px 40px 40px 40px',
        backgroundColor: 'rgba(224, 224, 224, 1);',
    },
    container: {
        maxHeight: '100%',
        backgroundColor: '#FFFFFF',
    },
    editIcon: {
        opacity: 0,
    },
    removeIcon: {
        opacity: 0,
    },

    rowContainer: {
        cursor: 'pointer',
        '&:hover ': {
            backgroundColor: '#eeeeee',
            transition: 'ease-in 200ms',
            '& button': {
                opacity: 1,
                transition: 'ease-in 200ms',
            },
        },
    },
    visuallyHidden: {
        border: 0,
        clip: 'rect(0 0 0 0)',
        height: 1,
        margin: -1,
        overflow: 'hidden',
        padding: 0,
        position: 'absolute',
        top: 20,
        width: 1,
    },
    sortableIcon: {
        color: 'rgb(0 0 0) !important',
        marginTop: 2,
        fontSize: 20,
        '&.active': {
            color: 'black',
        },
    },
    fab: {
        position: 'absolute',
        bottom: 40,
        right: 40,
    },
    viewDetailContainer: {
        display: 'flex',
    },
    viewDetails: {
        fontSize: 14,
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        color: '#484848',
    },
    label: {
        fontSize: 14,
        color: '#484848',
    },
});

type Order = 'asc' | 'desc';

const DataTable: FunctionComponent<DataTableProps> = (props) => {
    const {
        columns,
        data,
        sortOrder,
        handleDeleteCase,
        onClickRow,
        deleteFlag,
        editFlag,
        sortBy,
        setSortColumn,
        setSortOrder,
        removeFlag,
        removeTooltipLabel,
        handleRemove,
        renderDetails,
        showDetailsLabel,
    } = props;
    const [selectedDetailIds, setSelectedDetailIds] = useState<string[]>([]);
    const [showBackToTop, setShowBackToTop] = useState<boolean>(false);
    const classes = useStyles();
    const ref = useRef<any>(null);

    useEffect(() => {
        const scrollRef = ref.current;
        if (scrollRef) {
            scrollRef.addEventListener('scroll', handleScroll);
        }
        return () => {
            scrollRef.removeEventListener('scroll', handleScroll);
        };
    });

    function stableSort(array: any, comparator: any) {
        const stabilizedThis = array.map((el: any, index: any) => [el, index]);
        stabilizedThis.sort((a: any, b: any): any => {
            const order = comparator(a[0], b[0]);
            if (order !== 0) return order;
            return a[1] - b[1];
        });
        return stabilizedThis.map((el: any) => el[0]);
    }

    const createSortHandler = (property: any) => (event: any) => {
        handleRequestSort(event, property);
    };

    const handleRequestSort = (event: React.MouseEvent<unknown>, property: any) => {
        const isAsc = sortBy === property && sortOrder === 'asc';

        if (setSortColumn && setSortOrder) {
            setSortColumn(property);
            setSortOrder(isAsc ? 'desc' : 'asc');
        }
    };

    function getComparator<Key extends keyof any>(
        order: Order,
        orderBy: Key,
    ): (a: { [key in Key]: any }, b: { [key in Key]: any }) => number {
        return order === 'desc'
            ? (a, b) => descendingComparator(a, b, orderBy)
            : (a, b) => -descendingComparator(a, b, orderBy);
    }

    function descendingComparator<T>(a: T, b: T, orderBy: keyof T) {
        const comparedValues = {
            aValue: a[orderBy] + '',
            bValue: b[orderBy] + '',
        };
        if (typeof a[orderBy] === 'string' && typeof b[orderBy] === 'string') {
            comparedValues.aValue = '' + comparedValues.aValue.toLowerCase();
            comparedValues.bValue = '' + comparedValues.bValue.toLowerCase();
        }
        if (comparedValues.bValue < comparedValues.aValue) {
            return -1;
        }
        if (comparedValues.bValue > comparedValues.aValue) {
            return 1;
        }
        return 0;
    }
    const scrollToTop = () => {
        ref.current.scrollTo({ top: 0, behavior: 'smooth' }); //scroll to the top of the table
    };
    const handleScroll = (event: any) => {
        if (event?.target?.scrollTop > 200 && !showBackToTop) {
            setShowBackToTop(true);
        } else if (event?.target?.scrollTop === 0) {
            setShowBackToTop(false);
        }
    };
    const handleShowDetailsPanel = (id: string, showDetails: boolean) => {
        if (showDetails) {
            setSelectedDetailIds([...selectedDetailIds, id]);
        } else {
            setSelectedDetailIds(selectedDetailIds.filter((item) => item !== id));
        }
    };
    return (
        <AutoSizer disableWidth className="fullWidth">
            {({ height }) => (
                <Box style={{ height }}>
                    <TableContainer className={classes.container} ref={ref}>
                        <Table stickyHeader aria-label="sticky table">
                            <TableHead>
                                <TableRow>
                                    {columns?.map((column: any) => {
                                        return (
                                            <TableCell
                                                key={column.id}
                                                align={column.align}
                                                style={{
                                                    minWidth: column.minWidth,
                                                    fontWeight: 600,
                                                    fontSize: '14px',
                                                    color: '#484848',
                                                }}
                                                sortDirection={sortBy === column.field ? sortOrder : false}
                                            >
                                                <TableSortLabel
                                                    active={sortBy === column.field}
                                                    direction={sortBy === column.field ? sortOrder : 'asc'}
                                                    onClick={createSortHandler(column.field)}
                                                    IconComponent={ArrowDown}
                                                    classes={{
                                                        //Override with the active class of sortable icon
                                                        icon: classes.sortableIcon,
                                                    }}
                                                >
                                                    {column.label}
                                                    {sortBy === column.field ? (
                                                        <span className={classes.visuallyHidden}>
                                                            {sortOrder === 'desc'
                                                                ? 'sorted descending'
                                                                : 'sorted ascending'}
                                                        </span>
                                                    ) : null}
                                                </TableSortLabel>
                                            </TableCell>
                                        );
                                    })}
                                    {showDetailsLabel && <TableCell />}
                                    <TableCell />
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {stableSort(data, getComparator(sortOrder, sortBy)).map(
                                    (row: { [x: string]: any; id: any }) => {
                                        const showDetails = selectedDetailIds.some((id) => id === row.id);
                                        return (
                                            <>
                                                <TableRow
                                                    className={classes.rowContainer}
                                                    hover
                                                    tabIndex={-1}
                                                    key={uuid()}
                                                    onClick={(e: MouseEvent<HTMLElement>) => {
                                                        return onClickRow(row.id, e);
                                                    }}
                                                >
                                                    {columns?.map((column: any) => {
                                                        const valueObj: any = { value: row };
                                                        const valueWithType = column.id.split('|');
                                                        const columnId = valueWithType[0];
                                                        const columnType = valueWithType[1];
                                                        const objectTree = columnId.split('.');
                                                        if (objectTree.length > 0) {
                                                            objectTree.forEach((element: string) => {
                                                                valueObj.value = valueObj.value[element];
                                                            });
                                                        } else {
                                                            valueObj.value = row[columnId];
                                                        }
                                                        if (valueObj.value !== null && valueObj.value !== undefined) {
                                                            if (
                                                                valueObj.value !== null &&
                                                                valueObj?.value !== undefined
                                                            ) {
                                                                switch (columnType) {
                                                                    case 'date':
                                                                        valueObj.value = moment(
                                                                            valueObj?.value as string,
                                                                        ).format('L');
                                                                        break;
                                                                    case 'time':
                                                                        valueObj.value = moment(
                                                                            valueObj?.value as string,
                                                                        ).format('LT');
                                                                        break;
                                                                    case 'currency':
                                                                        valueObj.value = displayCurrencyAmount(
                                                                            valueObj?.value,
                                                                        );
                                                                        break;
                                                                    case 'timeRange':
                                                                        if (valueObj?.value.end) {
                                                                            valueObj.value =
                                                                                moment(
                                                                                    valueObj?.value.start as string,
                                                                                ).format('LT') +
                                                                                ' - ' +
                                                                                moment(
                                                                                    valueObj?.value.end as string,
                                                                                ).format('LT');
                                                                        } else {
                                                                            valueObj.value = moment(
                                                                                valueObj?.value.start as string,
                                                                            ).format('LT');
                                                                        }

                                                                        break;

                                                                    default:
                                                                        break;
                                                                }
                                                            }
                                                        }
                                                        return (
                                                            <TableCell
                                                                key={columnId}
                                                                align={column.align}
                                                                style={{ maxWidth: column.maxWidth }}
                                                                className={classes.label}
                                                            >
                                                                {valueObj.value}
                                                            </TableCell>
                                                        );
                                                    })}
                                                    {showDetailsLabel && (
                                                        <TableCell>
                                                            <div
                                                                className={classes.viewDetailContainer}
                                                                onClick={(e: MouseEvent<HTMLElement>) => {
                                                                    e.stopPropagation();
                                                                    handleShowDetailsPanel(row.id, !showDetails);
                                                                }}
                                                            >
                                                                <Typography className={classes.viewDetails}>
                                                                    {showDetailsLabel}
                                                                </Typography>
                                                                <IconButton>
                                                                    {showDetails ? (
                                                                        <KeyboardArrowUp />
                                                                    ) : (
                                                                        <KeyboardArrowDown />
                                                                    )}
                                                                </IconButton>
                                                            </div>
                                                        </TableCell>
                                                    )}
                                                    <TableCell>
                                                        {editFlag && (
                                                            <IconButton className={classes.editIcon}>
                                                                <EditIcon />
                                                            </IconButton>
                                                        )}
                                                        {deleteFlag && (
                                                            <IconButton
                                                                onClick={(e: MouseEvent<HTMLElement>) => {
                                                                    handleDeleteCase && handleDeleteCase(row.id, e);
                                                                }}
                                                                className={classes.removeIcon}
                                                            >
                                                                <Delete />
                                                            </IconButton>
                                                        )}
                                                        {removeFlag && (
                                                            <Tooltip
                                                                title={
                                                                    removeTooltipLabel ? removeTooltipLabel : 'Remove'
                                                                }
                                                                placement="top"
                                                                style={{ fontSize: '10px' }}
                                                            >
                                                                <IconButton
                                                                    onClick={(e: MouseEvent<HTMLElement>) => {
                                                                        handleRemove && handleRemove(row.id, e);
                                                                    }}
                                                                    className={classes.removeIcon}
                                                                >
                                                                    <Close />
                                                                </IconButton>
                                                            </Tooltip>
                                                        )}
                                                    </TableCell>
                                                </TableRow>
                                                <TableRow>
                                                    <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={6}>
                                                        <Collapse in={showDetails} timeout="auto">
                                                            {renderDetails && renderDetails(row)}
                                                        </Collapse>
                                                    </TableCell>
                                                </TableRow>
                                            </>
                                        );
                                    },
                                )}
                            </TableBody>
                            {showBackToTop && (
                                <Tooltip title="Back to Top" placement="top">
                                    <Fab
                                        size="small"
                                        className={classes.fab}
                                        aria-label="Back to Top"
                                        onClick={scrollToTop}
                                    >
                                        <BackToTopArrow color="primary" />
                                    </Fab>
                                </Tooltip>
                            )}
                        </Table>
                    </TableContainer>
                </Box>
            )}
        </AutoSizer>
    );
};

export default DataTable;
